// src/utils/progressionEvidence.js
//
// Pass 77 — the ONE canonical progression-evidence contract.
//
// PROGRESSION IS A DETERMINISTIC, DERIVED PROJECTION OF CANONICAL HUMAN REVIEW + DELIVERY EVIDENCE.
// AI IS NON-AUTHORITATIVE FOR PROGRESSION. This module never reads `video.aiReview.*` and never infers a
// signal from free text. It is a PURE read model (no Firestore, no persistence, no new store) over:
//   · reviewed HUMAN `video.analysis`  → getAnalysisSignals(...)  (Detect→Cause→Effect→Correct→Train)
//   · top-level `lessonInstances`      → buildLessonOutcomeSignals(...) (targetSkillId / lessonOutcome / next-skill)
//
// Invariants (Pass 77):
//   · sport is EXPLICIT (the caller's selected/requested sport); only that sport's evidence is considered.
//   · a reviewed video enters only when status === 'reviewed' (pending never enters); unknown sport degrades.
//   · each canonical SOURCE (one video / one lesson) contributes to a given skill focus AT MOST ONCE (no
//     aggregate+raw double-amplification); DISTINCT sources reinforcing the same focus form legitimate recurrence.
//   · currentLevel is derived ONLY from EXPLICIT leveled-skill evidence (canonical `skills.js` `level`), never
//     from review COUNT or terrain strings; when there is no such evidence it is '' (unknown) with basis 'none'.
//   · ZERO evidence → NO winner / NO recommendation (never a fabricated "Balanced/Centered Stance" default).
//   · deterministic: independent of input array ordering; identical evidence → identical result.

import { normalizeSportName } from './drillTemplates';
import { getAnalysisSignals, normalizeReviewAnalysis } from './reviewAnalysis';
import { buildLessonOutcomeSignals } from './lessonOutcomeSignals';
import { PROGRESSION_LEVELS } from './progressionConstants';

// signalKey (from getAnalysisSignals) → canonical movement focus. HUMAN structured analysis only.
// Pass 77 REPAIR — each mapping resolves to a STABLE canonical `skillId` (from skills.js). This is the domain
// authoritatively naming WHICH skill the human signal is about, so the engine resolves the exact skill by id and
// never substitutes an unrelated first-in-track skill. Each skillId is the unique canonical skill whose name +
// category matches, in the track implied by disciplineHint (all_mountain_fundamentals→*_all_mountain_fundamentals;
// carving→*_carving_branch; freeride_powder→*_freeride_powder_branch; else the core progression track).
export const SIGNAL_MAPPINGS = {
  skiing: {
    quiet_upper_body: { movementCategory: 'rotation', skillFocus: 'Upper-Lower Body Separation', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_all_mountain_upper_lower_separation' },
    upper_body_rotation: { movementCategory: 'rotation', skillFocus: 'Upper-Lower Body Separation', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_all_mountain_upper_lower_separation' },
    aft_balance: { movementCategory: 'balance', skillFocus: 'Balanced Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_all_mountain_balanced_stance' },
    lateral_balance: { movementCategory: 'balance', skillFocus: 'Balanced Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_all_mountain_balanced_stance' },
    weak_outside_ski: { movementCategory: 'pressure', skillFocus: 'Pressure Control Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_intermediate_pressure_control' },
    earlier_pressure_build: { movementCategory: 'pressure', skillFocus: 'Pressure Control Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_intermediate_pressure_control' },
    pressure_management: { movementCategory: 'pressure', skillFocus: 'Pressure Management Through the Arc', disciplineHint: 'all_mountain_fundamentals', skillId: 'ski_all_mountain_pressure_management' },
    late_edge_change: { movementCategory: 'edging', skillFocus: 'Edge Engagement Control', disciplineHint: 'carving', skillId: 'ski_intermediate_edge_engagement' },
    smoother_edge_change: { movementCategory: 'edging', skillFocus: 'Early Edge Engagement', disciplineHint: 'carving', skillId: 'ski_upper_intermediate_edge_engagement' },
    rounder_turn_shape: { movementCategory: 'timing_coordination', skillFocus: 'Turn Shape Control', disciplineHint: 'carving', skillId: 'ski_intermediate_turn_shape_control' },
    unfinished_turn: { movementCategory: 'timing_coordination', skillFocus: 'Turn Shape Control', disciplineHint: 'carving', skillId: 'ski_intermediate_turn_shape_control' },
    inconsistent_turn_shape: { movementCategory: 'timing_coordination', skillFocus: 'Turn Shape Control', disciplineHint: 'carving', skillId: 'ski_intermediate_turn_shape_control' },
    terrain_adaptation: { movementCategory: 'freeride_terrain', skillFocus: 'Terrain Adaptation', disciplineHint: 'freeride_powder', skillId: 'ski_advanced_terrain_adaptation' },
  },
  snowboarding: {
    counter_rotation: { movementCategory: 'rotation', skillFocus: 'Lower-Body Steering', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_lower_body_steering' },
    quieter_upper_body: { movementCategory: 'rotation', skillFocus: 'Lower-Body Steering', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_lower_body_steering' },
    lower_body_steering: { movementCategory: 'rotation', skillFocus: 'Lower-Body Steering', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_lower_body_steering' },
    disconnected_upper_lower_body: { movementCategory: 'rotation', skillFocus: 'Lower-Body Steering', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_lower_body_steering' },
    back_foot_dominance: { movementCategory: 'balance', skillFocus: 'Centered Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_centered_stance' },
    centered_stance: { movementCategory: 'balance', skillFocus: 'Centered Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_centered_stance' },
    stacked_alignment: { movementCategory: 'balance', skillFocus: 'Centered Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_centered_stance' },
    centered_mobile_position: { movementCategory: 'balance', skillFocus: 'Centered Stance Basics', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_centered_stance' },
    limited_edge_commitment: { movementCategory: 'edging', skillFocus: 'Edge Commitment for Carving', disciplineHint: 'carving', skillId: 'snowboard_carving_edge_commitment' },
    stronger_edge_commitment: { movementCategory: 'edging', skillFocus: 'Edge Commitment for Carving', disciplineHint: 'carving', skillId: 'snowboard_carving_edge_commitment' },
    patient_transition: { movementCategory: 'timing_coordination', skillFocus: 'Edge Change Timing', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_lower_intermediate_edge_change' },
    late_transition: { movementCategory: 'timing_coordination', skillFocus: 'Edge Change Timing', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_lower_intermediate_edge_change' },
    rounder_turn_shape: { movementCategory: 'timing_coordination', skillFocus: 'Turn Shape and Flow', disciplineHint: 'carving', skillId: 'snowboard_intermediate_turn_shape' },
    calm_board_flow: { movementCategory: 'pressure', skillFocus: 'Turn Shape and Flow', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_turn_shape' },
    smoother_pressure_management: { movementCategory: 'pressure', skillFocus: 'Turn Shape and Flow', disciplineHint: 'all_mountain_fundamentals', skillId: 'snowboard_all_mountain_turn_shape' },
    terrain_adaptation_confidence: { movementCategory: 'freeride_terrain', skillFocus: 'Terrain Adaptation Confidence', disciplineHint: 'freeride_powder', skillId: 'snowboard_advanced_terrain_adaptation' },
    limited_terrain_adaptation: { movementCategory: 'freeride_terrain', skillFocus: 'Terrain Adaptation Confidence', disciplineHint: 'freeride_powder', skillId: 'snowboard_advanced_terrain_adaptation' },
  },
};

// Human structured review-signal weights (issue > goal > strength). Lesson candidates carry their own weight.
const REVIEW_WEIGHT = { issue: 5, goal: 3, strength: 1 };

function levelIndex(level) {
  return PROGRESSION_LEVELS.indexOf(level);
}

function sportReviewedVideos(videos, sport) {
  return (videos || [])
    .filter((video) => video?.status === 'reviewed' && normalizeSportName(video?.sport || '') === sport)
    .map((video) => ({ video, t: video?.reviewedAt?.seconds || video?.createdAt?.seconds || 0, id: String(video?.id || '') }))
    // deterministic, order-independent: newest first, tie-break by stable id
    .sort((a, b) => b.t - a.t || a.id.localeCompare(b.id))
    .slice(0, 5)
    .map((x) => x.video);
}

// currentLevel is derived ONLY from a CURRENT DEMONSTRATED skill: the TARGET skill of a completed lesson whose
// human outcome confidence is `ready_to_progress` or `solid` (see lessonOutcomeSignals.demonstratedSkills). Its
// canonical `level` (from skills.js) is the current level. NEVER from:
//   · recommendedNextSkillId (that is the NEXT skill to work on, not an achieved/current level — guardrail 1/3)
//   · a review FOCUS skill (what to work on ≠ a demonstrated level)
//   · review count or terrain strings
// `needs_review`/low never demonstrates a skill. With no demonstrated-skill evidence, currentLevel is '' (unknown).
function deriveLevel(sport, lessonSignals) {
  const demonstrated = (lessonSignals?.demonstratedSkills || []).filter((s) => s?.level);
  if (demonstrated.length === 0) {
    return { currentLevel: '', levelBasis: 'none', levelConfidence: 0, levelSourceSkillId: '' };
  }
  // highest canonical level actually demonstrated
  const best = demonstrated.reduce((a, b) => (levelIndex(b.level) > levelIndex(a.level) ? b : a));
  return {
    currentLevel: best.level,
    levelBasis: 'lesson_demonstrated_skill',
    levelConfidence: best.confidence === 'ready_to_progress' ? 0.6 : 0.45,
    levelSourceSkillId: best.skillId,
  };
}

function emptyResult(sport) {
  return {
    sport,
    hasEvidence: false,
    evidence: [],
    candidates: [],
    winner: null,
    currentLevel: '',
    levelBasis: 'none',
    levelConfidence: 0,
    levelSourceSkillId: '',
    lessonSignals: { completedLessonCount: 0, reinforcedSkills: [], nextSkillHints: [], demonstratedSkills: [], progressionCandidates: [], summaryText: '' },
    recommendationReason: '',
    confidenceScore: 0,
    sourceSignals: [],
    unlockedBranchHints: [],
  };
}

export function buildProgressionEvidence({ videos = [], lessonInstances = [], lessonSignals: providedLessonSignals = null, sport = '' } = {}) {
  const targetSport = normalizeSportName(sport || '');
  if (!targetSport || !SIGNAL_MAPPINGS[targetSport]) return emptyResult(targetSport);
  const mappings = SIGNAL_MAPPINGS[targetSport];

  const candidates = new Map(); // candKey (canonical skillId) -> { skillId, movementCategory, skillFocus, disciplineHint, score, sourceSignals:[] }
  const evidence = [];
  // SEMANTIC dedupe identity (guardrail 2): sourceType:sourceId:signalType:signalKey. The SAME semantic signal
  // from the SAME canonical source entering through two helper layers counts ONCE. DIFFERENT signals (a different
  // issue/goal/strength, or the same key from a DIFFERENT source) each count — legitimate recurrence. A single
  // review may legitimately carry an issue AND a goal AND a strength; none of those is collapsed away.
  const seenEvidenceKeys = new Set();

  function contribute(candKey, meta, evidenceKey, weight, signal) {
    if (seenEvidenceKeys.has(evidenceKey)) return; // same semantic signal via two layers → once
    seenEvidenceKeys.add(evidenceKey);
    const cand = candidates.get(candKey) || {
      skillId: meta.skillId || '',
      movementCategory: meta.movementCategory,
      skillFocus: meta.skillFocus,
      disciplineHint: meta.disciplineHint,
      score: 0,
      sourceSignals: [],
    };
    cand.score += weight;
    cand.sourceSignals.push(signal);
    candidates.set(candKey, cand);
    evidence.push(signal);
  }

  // ── HUMAN REVIEW evidence (each reviewed video is ONE source) ──
  sportReviewedVideos(videos, targetSport).forEach((video) => {
    const sourceId = `review:${String(video?.id || '')}`;
    const occurredAt = video?.reviewedAt?.seconds || video?.createdAt?.seconds || 0;
    const analysis = normalizeReviewAnalysis(video, video?.analysis);
    const signals = getAnalysisSignals(analysis);
    [['issues', 'issue'], ['goals', 'goal'], ['strengths', 'strength']].forEach(([band, signalType]) => {
      (signals?.[band] || []).forEach((item) => {
        const meta = mappings[item.key];
        if (!meta) return;
        const evidenceKey = `${sourceId}:${signalType}:${item.key}`; // semantic: sourceType:sourceId:signalType:signalKey
        // candidates group by the CANONICAL skillId the mapping names (two same-named skills in different tracks are
        // genuinely distinct candidates); fall back to category:skillFocus only if a mapping lacks a skillId.
        const candKey = meta.skillId || `${meta.movementCategory}:${meta.skillFocus}`;
        contribute(candKey, meta, evidenceKey, REVIEW_WEIGHT[signalType], {
          evidenceId: evidenceKey,
          sourceType: 'review', sourceId, sport: targetSport, signalType,
          skillId: meta.skillId || '', movementCategory: meta.movementCategory, skillFocus: meta.skillFocus,
          signalKey: item.key, label: item.label, polarity: signalType === 'strength' ? 'positive' : 'focus',
          weight: REVIEW_WEIGHT[signalType], occurredAt, provenance: 'human_review_analysis',
        });
      });
    });
  });

  // ── LESSON / DELIVERY evidence (each lesson candidate is a canonical source) ──
  // Reuse pre-built lessonSignals when the caller already computed them for this exact sport; else build now.
  const lessonSignals = (providedLessonSignals && Array.isArray(providedLessonSignals.progressionCandidates))
    ? providedLessonSignals
    : buildLessonOutcomeSignals(lessonInstances, { sport: targetSport });
  (lessonSignals.progressionCandidates || []).forEach((c, idx) => {
    // a lesson candidate's signalKey IS the canonical target/next skillId (from lessonOutcomeSignals) — the domain
    // already knows exactly which skill it means.
    const meta = { skillId: c.signalKey, movementCategory: c.movementCategory, skillFocus: c.skillFocus, disciplineHint: c.disciplineHint };
    const lessonId = c.sourceLessonId || `idx${idx}`; // distinct lessons on the same skill = recurrence, not dedupe
    const sourceId = `lesson:${lessonId}`;
    const evidenceKey = `${sourceId}:${c.type}:${c.signalKey}`;
    const candKey = c.signalKey || `${c.movementCategory}:${c.skillFocus}`;
    contribute(candKey, meta, evidenceKey, c.weight || 1, {
      evidenceId: evidenceKey,
      sourceType: 'lesson', sourceId, sport: targetSport, signalType: c.type,
      skillId: c.signalKey || '', movementCategory: c.movementCategory, skillFocus: c.skillFocus,
      signalKey: c.signalKey, label: c.label || c.skillFocus, polarity: 'focus',
      weight: c.weight || 1, occurredAt: 0, provenance: 'human_lesson_outcome',
    });
  });

  const ranked = Array.from(candidates.values())
    .map((c) => ({ skillId: c.skillId, movementCategory: c.movementCategory, skillFocus: c.skillFocus, disciplineHint: c.disciplineHint, score: c.score, sourceSignals: c.sourceSignals }))
    .sort((a, b) => b.score - a.score || a.skillFocus.localeCompare(b.skillFocus));

  const winner = ranked[0] || null; // ZERO evidence → NO winner (no fabricated default)
  const { currentLevel, levelBasis, levelConfidence, levelSourceSkillId } = deriveLevel(targetSport, lessonSignals);

  const confidenceScore = winner
    ? Math.min(1, Number((winner.score / Math.max(4, winner.score + (ranked[1]?.score || 0))).toFixed(2)))
    : 0;

  const sourceLabels = winner ? winner.sourceSignals.slice(0, 2).map((s) => s.label).filter(Boolean) : [];
  const recommendationReason = winner
    ? (sourceLabels.length > 0
      ? `You are working on ${winner.skillFocus} because your canonical review and lesson evidence keeps highlighting ${sourceLabels.join(' and ')}.`
      : `You are working on ${winner.skillFocus} based on your canonical coaching evidence.`)
    : '';

  return {
    sport: targetSport,
    hasEvidence: ranked.length > 0,
    evidence,
    candidates: ranked,
    winner,
    currentLevel, levelBasis, levelConfidence, levelSourceSkillId,
    lessonSignals,
    recommendationReason,
    confidenceScore,
    sourceSignals: winner ? winner.sourceSignals : [],
    unlockedBranchHints: winner && levelIndex(currentLevel) >= levelIndex('intermediate')
      ? [winner.disciplineHint].filter(Boolean)
      : [],
  };
}
